import React, { useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core'
import classNames from 'classnames'
import type { StyleClasses } from '../../styles'
import Avatar from '../Avatar/Avatar'
import type { AvatarProps } from '../Avatar/Avatar'
import Tooltip from '../Tooltip'

const useStyles = makeStyles<Theme, EstimatorAvatarProps>(
  () => ({
    img: {
      borderRadius: '50%',
      objectFit: 'cover',
      height: ({ height }) => `${height || '28px'} !important`,
      width: ({ width }) => `${width || '28px'} !important`,
      marginRight: '0px !important'
    },
    avatar: {
      padding: '0px !important',
      boxShadow: ({ borderColor }) => (borderColor ? `0 0 0 2px white, 0 0 0 4px ${borderColor} !important` : undefined)
    }
  }),
  { name: 'EstimatorAvatar' }
)

export interface EstimatorAvatarUser {
  id?: string
  firstName?: string
  lastName?: string
}

export interface EstimatorAvatarProps extends AvatarProps {
  classes?: StyleClasses<typeof useStyles>
  userPictures: {
    [key: string]: string
  }
  user?: EstimatorAvatarUser
  borderColor?: string
  hideTooltip?: boolean
  /**
   * Whether the underlying user is still active. When false, the avatar is
   * rendered with the same greyed treatment used on the Activity feed:
   * image is desaturated and initials are drawn with the neutral palette.
   * The tooltip is appended with " (deactivated)".
   */
  active?: boolean
  /**
   * How to render when no user is supplied (or `user.id` is missing).
   * - `'placeholder'` (default): legacy behavior — renders "PaintScout User" initials.
   * - `'blank'`: renders nothing (`null`). Use when the owner was intentionally
   *   removed (e.g. via the "Remove estimator" deactivation flow).
   */
  renderEmpty?: 'placeholder' | 'blank'
}

function addAuth0Prefix(id: string) {
  if (!id) return id
  return id.includes('auth0|') ? id : `auth0|${id}`
}

/**
 * Avatars render tiny (28px by default) but `customAvatar.cdnValue` points at the full-resolution
 * upload. On a list with many avatars (e.g. every card on the Deals board) that's potentially hundreds
 * of multi-MB images decoded into memory at once — a real OOM driver on low-memory iPads (#21020).
 *
 * For Cloudinary delivery URLs we can ask the CDN for a face-cropped thumbnail sized to how the avatar
 * actually renders (capped at 2x for retina). S3/other URLs don't support URL-based resizing, so they
 * are returned untouched. URLs that already carry a transformation segment are left alone to avoid
 * double-processing.
 */
const CLOUDINARY_UPLOAD_MARKER = '/image/upload/'
function buildAvatarThumbUrl(url: string, size: number): string {
  if (!url) return url
  const markerIndex = url.indexOf(CLOUDINARY_UPLOAD_MARKER)
  if (markerIndex === -1) return url

  const afterMarker = url.slice(markerIndex + CLOUDINARY_UPLOAD_MARKER.length)
  // already transformed (segment looks like `c_fill,...` / `w_64/`) — don't stack another transform
  if (/^[a-z]{1,3}_[^/]+\//.test(afterMarker)) return url

  const transform = `c_fill,g_face,w_${size},h_${size},dpr_2.0,f_auto,q_auto/`
  return url.slice(0, markerIndex + CLOUDINARY_UPLOAD_MARKER.length) + transform + afterMarker
}

function parseAvatarSize(...values: Array<string | number | undefined>): number {
  for (const value of values) {
    const parsed = parseInt(String(value ?? ''), 10)
    if (parsed > 0) return parsed
  }
  return 28
}

export default function EstimatorAvatar(props: EstimatorAvatarProps) {
  const {
    userPictures,
    user,
    borderColor,
    hideTooltip,
    active = true,
    renderEmpty = 'placeholder',
    ...avatarProps
  } = props
  const classes = useStyles(props)
  const theme = useTheme()
  const [imgLoading, setImgLoading] = useState(false)

  if (!user?.id && renderEmpty === 'blank') {
    return null
  }

  const userPicture = user?.id ? userPictures?.[addAuth0Prefix(user.id)] : null
  const avatarSize = parseAvatarSize(props.width, props.height)
  const thumbSrc = userPicture ? buildAvatarThumbUrl(userPicture, avatarSize) : userPicture
  const userName = user?.id
    ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'PaintScout User'
    : 'PaintScout User'

  const inactiveAvatarColor = theme.paintscout.palette.brand.Neutral.P94
  const avatarColor = active
    ? borderColor ?? props.avatarColor
    : inactiveAvatarColor ?? borderColor ?? props.avatarColor
  const tooltipContent = active ? userName : `${userName} (deactivated)`

  // The initials variant gets consumer styles via `{...avatarProps}`; the image variant used to
  // silently drop them, so `style={{ opacity: 0.5 }}` greyed out initials but not photos (ENG-1048).
  const styleProp = (avatarProps as { style?: React.CSSProperties }).style
  const targetOpacity = styleProp?.opacity ?? 1

  const Picture = userPicture ? (
    <img
      onClick={props.onClick}
      className={classNames(classes.img, 'disable-click')}
      src={thumbSrc}
      // defer fetch/decode of off-screen avatars (e.g. long Deals board) to avoid loading hundreds
      // of images into memory at once — a key OOM driver on low-memory iPads (#21020)
      loading="lazy"
      decoding="async"
      style={{
        ...styleProp,
        // keep the fade-in gate authoritative over any consumer opacity
        opacity: imgLoading ? 0 : targetOpacity,
        transition: 'opacity 0.2s ease-in',
        boxShadow: borderColor
          ? `0 0 0 2px white, 0 0 0 4px ${borderColor}` // White outline followed by colored outline
          : undefined,
        filter: !active ? 'contrast(50%) brightness(1.4)' : styleProp?.filter
      }}
      alt={userName}
      onLoad={() => {
        setImgLoading(false)
      }}
    />
  ) : (
    <Avatar
      className={classNames(classes.avatar, 'disable-click')}
      backgroundColor="#FFFFFF"
      avatarColor={avatarColor}
      disableTooltip
      filled
      noTransform
      width="28px"
      height="28px"
      fontSize={'12px'}
      {...avatarProps}
    >
      {/* Limit the username to two words so we only get two initials */}
      {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
    </Avatar>
  )

  if (hideTooltip) {
    return Picture
  }
  return (
    <Tooltip hideIcon content={tooltipContent} enterDelay={500} enterNextDelay={500}>
      {Picture}
    </Tooltip>
  )
}
